import React from 'react';
import { Box, Button, Center, CircularProgress, Flex, Heading, Image, Text, VStack } from '@chakra-ui/react';
import { useDropzone } from 'react-dropzone';

import billUploadImg from '../../../../assets/images/bill_upload_icon.svg';
import bubbleBgImg from '../../../../assets/images/bubble_bg.svg';
import { useViewportType } from '../../../../common/hooks/use-viewport-type';
import { ManualBillUpload } from './ManualBillUpload';
import { BILL_STATUS_TO_CONFIG, BillUploadStatus } from './upload-bill-helpers';
import { UploadBillInfoPopover } from './UploadBillInfoPopover';
import { BillToUpload, UploadBillsProps, useUploadBill } from './use-upload-bill';

export function UploadBillContents({ onResetBills, onClose, billUrl }: UploadBillsProps) {
  const { isUploading, uploadedBill, handleGetActionFromStatus, failedBillS3Path, onDrop, onFinishManualBillUpload } =
    useUploadBill({
      onResetBills,
      onClose,
      billUrl,
    });

  return (
    <Box>
      <Center mb={6}>
        {!!failedBillS3Path && (
          <ManualBillUpload billS3Path={failedBillS3Path} onFinishUpload={onFinishManualBillUpload} />
        )}

        {!!isUploading && (
          <BillUploadStatusScreen uploadedBill={uploadedBill} handleGetActionFromStatus={handleGetActionFromStatus} />
        )}

        {!failedBillS3Path && !isUploading && <BillUploadDropSection onDrop={onDrop} />}
      </Center>
    </Box>
  );
}

type BillUploadStatusProps = {
  uploadedBill: BillToUpload;
  handleGetActionFromStatus: () => void;
};

function BillUploadStatusScreen({ uploadedBill, handleGetActionFromStatus }: BillUploadStatusProps) {
  const modalContentConfig = BILL_STATUS_TO_CONFIG[uploadedBill.status];
  const {
    icon,
    buttonText,
    messageText: { heading, body },
  } = modalContentConfig;
  return (
    <VStack>
      <Center>
        <Image src={bubbleBgImg} />
        <Image position="absolute" src={icon} />

        {[
          BillUploadStatus.InProgressPreSigning,
          BillUploadStatus.InProgressUploading,
          BillUploadStatus.InProgressProcessing,
        ].includes(uploadedBill.status) && (
          <CircularProgress position="absolute" size="25px" isIndeterminate color="primaryBranding.500" />
        )}
      </Center>
      <Heading textAlign="center">{heading}</Heading>
      <Text align="center">{body}</Text>
      {buttonText && (
        <Button
          data-testid="finished-upload-btn"
          rounded={20}
          w="40%"
          variant="solid"
          colorScheme="dusk100"
          onClick={handleGetActionFromStatus}
        >
          {buttonText}
        </Button>
      )}
    </VStack>
  );
}

type BillUploadDropSectionProps = {
  onDrop: (acceptedFiles: File[]) => void;
};

function BillUploadDropSection({ onDrop }: BillUploadDropSectionProps) {
  const { isDesktopViewport } = useViewportType();
  const { getRootProps, getInputProps } = useDropzone({ onDrop, accept: 'application/pdf', multiple: false });

  return (
    <Center
      data-testid="bill-upload-dropzone"
      flexDirection="column"
      border="2px"
      borderStyle="dashed"
      rounded={5}
      borderColor="day.500"
      bg="rgba(134, 181, 209, 0.2)"
      h="300px"
      w="80%"
      {...getRootProps()}
    >
      <input {...getInputProps()} />
      <Image mb={2} src={billUploadImg} alt="Upload icon" w="20%" />
      <Flex mb={2} align="center">
        <Heading mr={2} size="md">
          Electricity Bill
        </Heading>
        <UploadBillInfoPopover />
      </Flex>
      <Text mb={3} fontSize="sm">
        (original PDF only, no scans or photos)
      </Text>
      {isDesktopViewport && (
        <Text mb={5} fontSize="sm">
          Upload or drag and drop your file.
        </Text>
      )}
      <Button colorScheme="white" rounded={20} w="40%" variant="outline" mr={3} type="button">
        Upload
      </Button>
    </Center>
  );
}
